import { useCallback, useState } from 'react'
import { tenantUserInvitationSaveSpec, TenantUserRole } from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { toastError, toastSuccess } from '@/components/action/Toaster'
import { Button } from '@/components/action/Button'
import { Notice } from '@/components/action/Notice'
import { TextBox } from '@/components/scalar/TextBox'
import { TextArea } from '@/components/scalar/TextArea'
import { SelectBox } from '@/components/scalar/SelectBox'
import './TenantInvitationAdd.css'

const INVITATION_ROLE_OPTIONS: { label: string; value: TenantUserRole }[] = [
  { label: TenantUserRole[TenantUserRole.Owner], value: TenantUserRole.Owner },
  { label: TenantUserRole[TenantUserRole.Admin], value: TenantUserRole.Admin },
  { label: TenantUserRole[TenantUserRole.Viewer], value: TenantUserRole.Viewer },
]

type Props = {
  tenantId: string
  onAdded: () => void
}

export function TenantInvitationAdd({ tenantId, onAdded }: Props) {
  const api = useApi({ requireLogin: true })

  const [newEmail, setNewEmail] = useState('')
  const [newName, setNewName] = useState('')
  const [newRole, setNewRole] = useState<TenantUserRole>(TenantUserRole.Viewer)
  const [newMessage, setNewMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [invitationId, setInvitationId] = useState<number>()
  const [invitationName, setInvitationName] = useState<string>()
  const [generatedPasscode, setGeneratedPasscode] = useState<string>()

  const asEmailInvitation = newEmail.trim().length > 0

  //--------------------------------------------------------------------------------
  const handleAddInvitation = useCallback(async () => {
    setSubmitting(true)
    try {
      const res = await api.request(tenantUserInvitationSaveSpec, {
        path: { tenant: tenantId, invitation: 'new' },
        body: {
          invitation: {
            email: newEmail.trim() || undefined,
            name: newName.trim() || undefined,
            role: newRole,
            message: newMessage.trim() || undefined,
          },
        },
      })
      if (res.status === 200 && res.data?.invitation) {
        const invitation = res.data.invitation
        setInvitationId(invitation.id!)
        setInvitationName(invitation.name ?? undefined)
        if (invitation.passcodeText) {
          setGeneratedPasscode(invitation.passcodeText)
        } else {
          setGeneratedPasscode(undefined)
        }
        toastSuccess('Invitation added')
        setNewEmail('')
        setNewName('')
        setNewRole(TenantUserRole.Viewer)
        setNewMessage('')
        onAdded()
      } else {
        toastError(res.error ?? 'Failed to add invitation')
      }
    } catch {
      toastError('Failed to add invitation')
    } finally {
      setSubmitting(false)
    }
  }, [tenantId, newEmail, newName, newRole, newMessage, onAdded, toastError, toastSuccess])

  //--------------------------------------------------------------------------------
  const handleCopyPasscode = useCallback(() => {
    if (generatedPasscode == null) return
    void navigator.clipboard.writeText(`${tenantId}:${invitationId}:${generatedPasscode}`).then(
      () => toastSuccess('Passcode copied'),
      () => toastError('Could not copy'),
    )
  }, [generatedPasscode, toastError, toastSuccess])

  //--------------------------------------------------------------------------------
  const handleHidePasscode = useCallback(() => {
    setGeneratedPasscode(undefined)
    toastSuccess('Passcode hidden for safety')
  }, [toastSuccess])

  //--------------------------------------------------------------------------------
  return (
    <div className='TenantInvitationAdd'>
      <br />
      <h3>Invite someone</h3>

      <div className='FlexRow ItemsEnd FlexWrap Gap2'>
        <TextBox
          label='Email (optional)'
          width={240}
          value={newEmail}
          setValue={setNewEmail}
        />
        <TextBox
          label={'Name' + (asEmailInvitation ? ' (optional)' : '')}
          required={!asEmailInvitation}
          width={200}
          value={newName}
          setValue={setNewName}
        />
        <SelectBox<TenantUserRole>
          label='Role'
          options={INVITATION_ROLE_OPTIONS}
          valueToString={v => (v != null ? String(v) : '')}
          valueFromString={s => Number(s) as TenantUserRole}
          value={newRole}
          setValue={setNewRole}
        />
        <TextBox
          label='Message (optional)'
          width='30rem'
          disabled={!asEmailInvitation}
          value={newMessage}
          setValue={setNewMessage}
        />
        <Button
          label={asEmailInvitation
            ? 'Create email invitation'
            : 'Create passcode invitation'
          }
          icon={asEmailInvitation
            ? 'envelope'
            : 'unlock'
          }
          disabled={submitting || (!asEmailInvitation && !newName.trim())}
          onClick={() => void handleAddInvitation()}
        />
      </div>

      {generatedPasscode != null && (
        <div className='PasscodeBlock'>

          <div className='FlexRow ItemsCenter ContentCenter Gap2'>
            <Button
              label='Copy it'
              icon='copy'
              onClick={handleCopyPasscode}
            />
            <Button
              variant='primary'
              label='Hide it'
              icon='lock'
              onClick={handleHidePasscode}
            />
          </div>

          <Notice variant='info'>
            Here is your invitation code for '{invitationName}'. Do not use spaces.
          </Notice>

          <div className='PasscodeDisplay'>
            <span className='Part'>{tenantId}</span>
            :
            <span className='Part'>{invitationId}</span>
            :
            <span className='Part'>{generatedPasscode.slice(0, 3)}</span>
            <span className='Part'>{generatedPasscode.slice(3, 6)}</span>
          </div>

          <Notice variant='warning'>
            You will not see this passcode here again. Share it with the
            person you are inviting. Click &quot;Hide it&quot; when you are done.
          </Notice>

        </div>
      )}

    </div>
  )
}
